/* eslint-disable react/jsx-props-no-spreading */
/**
 * @description renders a generic dropdown field thats used for configuring personnel eligibility, access required, special access, etc.
 */

import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';

import NoResults from '@seccon/shared/ui/components/NoResults';
import withErrorBoundary from '@seccon/shared/ui/hocs/with-error-boundary';

const styles = {
  filterField: {
    backgroundColor: 'background.default'
  },
  error: {
    fontSize: '1rem'
  }
};

/**
 * @prop {string} id - form field id
 * @prop {boolean} isError - indicates if the dropdown field failed to load or not
 * @prop {boolean} isLoading - indicates if the dropdown field is being loaded still or not
 * @prop {string} label - the label for the dropdown field
 * @prop {{ label: string, value: any }[]} - the list of dropdown options (might be null|undefined while loading)
 */
const DropdownField = ({
  disableClearable,
  id,
  isError,
  isLoading,
  label,
  multiple,
  options,
  required,
  variant,
  disabled
}) => {
  const { control } = useFormContext();
  const emptyArrayRef = React.useRef([]);
  const defaultValue = multiple ? emptyArrayRef.current : null;

  if (isLoading) {
    return (
      <Skeleton animation='wave' variant='rectangular' height={40} />
    );
  }

  if (isError) {
    return (
      <NoResults Icon={WarningAmberIcon} sx={styles.error} variant='inline'>Server Unavailable</NoResults>
    );
  }

  return (
    <Controller
      control={control}
      name={id}
      render={({ field: { onChange, value } }) => {
        function isOptionEqualToValue (option) {
          if (multiple) {
            return value.some((entry) => entry.value === option.value);
          }

          return value.value === option.value;
        }

        return (
          <Autocomplete
            autoHighlight
            disableClearable={disableClearable}
            disabled={disabled}
            isOptionEqualToValue={(option) => isOptionEqualToValue(option)}
            multiple={multiple}
            options={options}
            onChange={(_, val) => onChange(val)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                required={required}
                size='small'
                sx={variant === 'filters' ? styles.filterField : null}
              />
            )}
            renderTags={!multiple ? null : (tagValue, getTagProps) => (
              tagValue.map((option, index) => (
                <Chip size='small' {...getTagProps({ index })} label={option.label} />
              ))
            )}
            value={value ?? defaultValue}
          />
        )
      }}
    />
  );
};

DropdownField.propTypes = {
  disableClearable: PropTypes.bool,
  id: PropTypes.string.isRequired,
  isError: PropTypes.bool,
  isLoading: PropTypes.bool,
  label: PropTypes.string.isRequired,
  multiple: PropTypes.bool,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    })
  ),
  required: PropTypes.bool,
  variant: PropTypes.string,
  disabled: PropTypes.bool
};

DropdownField.defaultProps = {
  disableClearable: false,
  isError: false,
  isLoading: false,
  multiple: false,
  options: [],
  required: false,
  variant: null,
  disabled: false
};

export default withErrorBoundary(DropdownField);
